import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import LuxonUtils from '@date-io/luxon';
import { MigrationPeriod } from './types';
import { DateTime } from 'luxon';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    paddingTop: theme.spacing(2),
    minWidth: 500,
  },
  datePickers: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

interface SetMigrationPeriodProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (migrationPeriod: MigrationPeriod) => void;
}

export const SetMigrationPeriod: React.FC<SetMigrationPeriodProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const classes = useStyles();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const isEndBeforeStart =
    startDate !== null && endDate !== null && endDate < startDate;
  const canConfirm = startDate !== null && endDate !== null && !isEndBeforeStart;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        <Typography variant="h6">Initiate IDP Migration</Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            When IDP migration is initiated, login options with RAC and FLUIG will be enabled
            for the specified period. After the period ends, login will only be possible through FLUIG.
          </Typography>
        </Box>

        <Typography variant="subtitle1" gutterBottom>
          Migration period
        </Typography>

        <MuiPickersUtilsProvider utils={LuxonUtils}>
          <Box className={classes.datePickers}>
            <KeyboardDatePicker
              disableToolbar
              variant="inline"
              format="yyyy/MM/dd"
              label="Start Date *"
              value={startDate}
              onChange={date => setStartDate(date ? (date as DateTime)?.toJSDate() ?? null : null)}
              autoOk
            />
            <KeyboardDatePicker
              disableToolbar
              variant="inline"
              format="yyyy/MM/dd"
              label="End Date *"
              value={endDate}
              onChange={date => setEndDate(date ? (date as DateTime)?.toJSDate() ?? null : null)}
              error={isEndBeforeStart}
              helperText={isEndBeforeStart ? 'End date must be after start date' : undefined}
              autoOk
            />
          </Box>
        </MuiPickersUtilsProvider>
      </DialogContent>

      <DialogActions style={{ justifyContent: 'flex-end', padding: '16px 24px' }}>
        <Button
          onClick={onClose}
          color="default"
          variant="outlined"
          style={{ marginRight: 8 }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm({ start: startDate!, end: endDate! })}
          color="primary"
          variant="contained"
          disabled={!canConfirm}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
